import React, { useState, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Button, { ILink } from 'tbk-components/src/components/Button';
import { IVideo } from 'tbk-components/src/components/Video';
import AnimatedArcs from '../AnimatedArcs';
import FormLightboxButton, {
	IFormLightboxButtonProps,
} from '../FormLightboxButton';

export interface IBgVideoButtonProps {
	playLabel: string;
	pauseLabel: string;
}

export interface IMainHeroVideoProps extends IBaseElementProps {
	preHeading?: string;
	heading?: string;
	description?: string;
	ctaButtonOne?: ILink;
	ctaButtonTwo?: ILink;
	formLightboxButton?: IFormLightboxButtonProps;
	/**
	 * @size resize
	 */
	backgroundImage?: IImage;
	backgroundVideo?: IVideo;
	autoPlay?: boolean;
	/**
	 * @noUI
	 */
	i18n: IBgVideoButtonProps;
	arcsPosition: 'Left' | 'Right';
	inverted?: boolean;
}

/**
 * Calian Main Hero - Background Image or Video
 * @icon cover-image
 * @block
 */
const MainHeroVideo: React.FC<IMainHeroVideoProps> = ({
	className,
	classNames = [],
	preHeading,
	heading,
	description,
	ctaButtonOne,
	ctaButtonTwo,
	formLightboxButton,
	backgroundImage,
	backgroundVideo,
	autoPlay = true,
	i18n,
	arcsPosition = 'Right',
	inverted = false,
}) => {
	const bgVideoRef = useRef(null);
	const bgVideoBtnRef = useRef(null);
	const [isVideoPlaying, setIsVideoPlaying] = useState(autoPlay);

	const handleVideoPlay = (videoRef) => {
		setIsVideoPlaying(true);
		videoRef.play();
	};

	const handleVideoPause = (videoRef) => {
		setIsVideoPlaying(false);
		videoRef.pause();
	};

	return (
		<div
			className={
				className ||
				cn('calian-main-hero', ...classNames, inverted && 'inverted')
			}
		>
			<div className="main-hero-video position-relative">
				<div className="main-hero-video__overlay-top"></div>
				<div className="main-hero-video__overlay-left"></div>
				<div className="main-hero-video__overlay-general"></div>
				<div className="main-hero-video__content container-fluid mw-1195px">
					<div className="row px-3">
						<div className="col-12 mw-641px p-0">
							{preHeading && (
								<p className="main-hero-video__pre-heading text-white fs-4 mb-4">
									{preHeading}
								</p>
							)}
							{heading && (
								<h1 className="main-hero-video__heading text-white mb-4">
									{heading}
								</h1>
							)}
							{description && (
								<p className="main-hero-video__description text-white subheading mb-5">
									{description}
								</p>
							)}
							<div className="main-hero-software__cta-buttons d-flex flex-column flex-sm-row justify-content-start pt-2">
								{ctaButtonOne && ctaButtonOne.label && (
									<Button
										className={cn(
											`btn btn-${
												inverted
													? 'secondary'
													: 'primary'
											}`,
										)}
										{...ctaButtonOne}
									/>
								)}
								{ctaButtonTwo && ctaButtonTwo.label && (
									<Button
										className="btn btn-light mt-3 mt-sm-0 ms-sm-3"
										{...ctaButtonTwo}
									/>
								)}
								{formLightboxButton?.formLightboxButton?.label
									?.length > 0 && (
									<FormLightboxButton
										{...formLightboxButton}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
				{!backgroundVideo && backgroundImage && (
					<Image
						{...backgroundImage}
						className="hero-banner__bg-img"
					/>
				)}
				{backgroundVideo && (
					<>
						<video
							src={backgroundVideo.src}
							className="hero-banner__bg-video"
							poster={backgroundImage?.src}
							autoPlay
							muted
							playsInline
							loop
							ref={bgVideoRef}
						/>

						<button
							className="hero-banner__bg-video-btn btn link-white position-absolute z-3 bottom-0 left-0 d-flex justify-content-center align-items-center fs-9 ms-3 ms-lg-5 mb-3 mb-lg-5 p-1"
							tabIndex={0}
							ref={bgVideoBtnRef}
							onClick={() => {
								const video =
									bgVideoRef.current! as HTMLVideoElement;
								video.paused
									? handleVideoPlay(video)
									: handleVideoPause(video);
							}}
						>
							{!isVideoPlaying ? (
								<>
									<span className="icon icon-play fs-8px me-2"></span>
									{i18n?.playLabel || 'Play'}
								</>
							) : (
								<>
									<span className="icon icon-pause fs-8px me-2"></span>
									{i18n?.pauseLabel || 'Pause'}
								</>
							)}
						</button>
					</>
				)}
				<AnimatedArcs
					arcsStyle={'Component - Two Lines'}
					arcsPosition={arcsPosition}
					arcOneColor={'var(--bs-primary)'}
					arcOneStrokeWidth={4}
					arcTwoColor={'var(--bs-white)'}
					arcTwoStrokeWidth={3}
					animationDelay={250}
					animationOffset={150}
				/>
			</div>
		</div>
	);
};

export default MainHeroVideo;
